import {
  OptionalStringParam,
  useParam,
} from "@redotech/react-router-util/param";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { AutomationForgeElementType } from "@redotech/redo-content-builder-common/forge/marketing-automation-forge-types";
import { AdvancedFlow } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import {
  FlowInProgress,
  PartialFlowStep,
} from "@redotech/redo-flows-common/flow-in-progress";
import {
  schemas,
  SchemaType,
} from "@redotech/redo-flows-common/schemas/schemas";
import { getTriggerDisplayName } from "@redotech/redo-flows-common/triggers";
import {
  StepType,
  stepTypeDisplayNames,
} from "@redotech/redo-flows-core-schema-common/common";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { saveAdvancedFlow } from "@redotech/redo-merchant-app-common/client/team";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { Flex } from "@redotech/redo-web/flex";
import {
  FormBuilderHeader,
  FormBuilderHeaderButton,
} from "@redotech/redo-web/form-builder/form-builder-header";
import { FormBuilderPageWrapper } from "@redotech/redo-web/form-builder/form-builder-page-wrapper";
import { Inspector } from "@redotech/redo-web/inspector";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import {
  dismissNotificationByKey,
  enqueueNotification,
  setNotificationRightOffset,
} from "@redotech/redo-web/notification-provider";
import { BgColorValue } from "@redotech/redo-web/theme/box";
import {
  UnsavedChangesModal,
  useUnsavedChangesGuard,
} from "@redotech/redo-web/unsaved-changes-guard";
import { filterTruthy } from "@redotech/util/array";
import { assertNever } from "@redotech/util/type";
import { ReactFlowProvider, useReactFlow } from "@xyflow/react";
import * as React from "react";
import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useResolvedPath } from "react-router-dom";
import { ElementDrop, Flowchart } from "./flow";
import * as flowEditorStyles from "./flow-editor.module.css";
import {
  AutomationForgeElementTypeWithDisabledTooltip,
  FlowEditorLeftPanel,
  StepSection,
} from "./flow-left-panel";
import { RightPanelWithAnimation } from "./flow-right-panel";
import { ConditionValueLabelsProvider } from "./nodes/condition-value-labels";
import { InvalidFlowStep, StepTypeDetailsProps } from "./steps";
import { RecognizeCondition } from "./steps/recognize-condition";
import { stepTypes } from "./steps/step-types";

export function getRightPanelClass(): string {
  const rightPanel = flowEditorStyles.rightPanel;
  if (!rightPanel) {
    throw new Error("Right panel style is missing");
  }
  return rightPanel;
}

export type NodeSelectionMode =
  | { type: "normal" }
  | { type: "node-copy"; onNodeSelect: (targetNodeId: string) => void }
  | { type: "node-copy-subtree"; onNodeSelect: (targetNodeId: string) => void };

interface CommonFlowEditorProps {
  navigationQueryParam?: string;
  automationId?: string | undefined;
  flowInProgress: FlowInProgress;
  partialFlow?: FlowInProgress;
  showPublishButton?: boolean;
  leftPanel?: {
    hide?: boolean;
    title?: string;
    description?: string;
    bgColor?: BgColorValue;
  };
  header?: { hide?: boolean; compact?: boolean; disableButtons?: boolean };
  rightPanel?: {
    bgColor?: BgColorValue;
    defaultContent?:
      | React.ReactNode
      | ((
          setFlowInProgress: React.Dispatch<
            React.SetStateAction<FlowInProgress | null>
          >,
        ) => React.ReactNode);
  };
  onCloseOverride?: () => void;
  closeButtonText?: string;
  elementDrop?: ElementDrop | undefined;
  deleteElementOverride?:
    | ((id: string, steps: PartialFlowStep[]) => PartialFlowStep[])
    | undefined;
  showMiniMap?: boolean;
  collapsibleNodes?: boolean;
  /** Confirm deletes that would also remove downstream descendants. */
  confirmCascadingDelete?: boolean;
  isModifiedFromStart?: boolean;
  hasUnsavedChanges?: boolean;
  /** Bump on each external `flowInProgress` replacement (e.g. JSON apply) to mark the flow modified. */
  externalFlowChangeSignal?: number;
  saveDisabled?: boolean;
  saveButtonText?: string | undefined;
  saveButtonDataTrackId?: string | undefined;
  additionalHeaderButtons?: {
    buttons:
      | FormBuilderHeaderButton[]
      | (({
          onSaveDisabled,
        }: {
          onSaveDisabled: boolean;
        }) => FormBuilderHeaderButton[]);
    position?: number;
  };
  onSaveSuccess?: () => void;
  getDisabledStepTypes?: (
    flowInProgress: FlowInProgress,
    invalidSteps?: InvalidFlowStep[],
  ) => AutomationForgeElementTypeWithDisabledTooltip[];
  manageInvalidStepNotifications?: (
    invalidSteps: InvalidFlowStep[],
    selectedStep: PartialFlowStep | null,
    selectedId: string | undefined,
    centerOnStep: (stepId: string) => void,
  ) => void;
  renderInvalidStepAlert?: (
    invalidSteps: InvalidFlowStep[] | undefined,
    stepTypes: AutomationForgeElementType[],
    onGoToStep?: (id: string) => void,
  ) => React.ReactNode;
}

type FlowEditorProps = (
  | { readonly: true }
  | {
      readonly?: false | undefined;

      stepOptions: StepSection[];
      disabledStepTypes?: AutomationForgeElementTypeWithDisabledTooltip[];
      setFlowInProgress: Dispatch<SetStateAction<FlowInProgress | null>>;
      // A false result keeps the editor dirty.
      onSaveOverride?: ({
        flowInProgress,
        signal,
      }: {
        flowInProgress: FlowInProgress;
        signal: AbortSignal;
      }) => Promise<{ saved: boolean } | undefined | void>;
    }
) &
  CommonFlowEditorProps;

const FlowEditorContent = memo(function FlowEditorContent({
  automationId,
  flowInProgress,
  partialFlow,
  showPublishButton,
  leftPanel,
  header,
  rightPanel,
  onCloseOverride,
  elementDrop,
  deleteElementOverride,
  showMiniMap,
  collapsibleNodes,
  confirmCascadingDelete,
  isModifiedFromStart,
  externalFlowChangeSignal,
  saveDisabled,
  saveButtonText,
  saveButtonDataTrackId,
  additionalHeaderButtons,
  onSaveSuccess,
  getDisabledStepTypes,
  manageInvalidStepNotifications,
  renderInvalidStepAlert,
  ...rest
}: FlowEditorProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const team = useContext(TeamContext);
  const client = useRequiredContext(RedoMerchantClientContext);

  const [modified, setModified] = useState(isModifiedFromStart ?? false);
  let hasUnsavedChanges = modified;

  if (rest.hasUnsavedChanges !== undefined) {
    hasUnsavedChanges = rest.hasUnsavedChanges;
  }

  /** This route's own path, which a mid-handoff redirect doesn't move. */
  const editorPath = useResolvedPath("");

  const { confirmOpen, discardChanges, keepEditing } = useUnsavedChangesGuard({
    isDirty: hasUnsavedChanges && !rest.readonly,
    // Panel selection lives in the `selected` query param.
    blockQueryChanges: rest.navigationQueryParam !== undefined,
    allowNavigation: ({ currentLocation, nextLocation }) => {
      if (
        rest.navigationQueryParam &&
        currentLocation.pathname === nextLocation.pathname
      ) {
        const currentTab = new URLSearchParams(currentLocation.search).get(
          rest.navigationQueryParam,
        );
        const nextTab = new URLSearchParams(nextLocation.search).get(
          rest.navigationQueryParam,
        );

        return currentTab === nextTab;
      }

      // Allow navigation between managed origin flows while saving.
      const [currentPathBeforeManaged, currentPathAfterManaged] =
        currentLocation.pathname.split("/managed/");
      const nextPathBeforeLastSlash = nextLocation.pathname
        .split("/")
        .slice(0, -1)
        .join("/");
      if (
        currentPathAfterManaged && // Currently on a managed flow
        currentPathBeforeManaged === nextPathBeforeLastSlash
      ) {
        return true;
      }

      // Step panels hand off to the email/SMS/Recover builders and come back.
      const returnPath = new URLSearchParams(nextLocation.search).get(
        "returnPath",
      );
      return returnPath === editorPath.pathname;
    },
  });

  const [selectionMode, setSelectionMode] = useState<NodeSelectionMode>({
    type: "normal",
  });
  const [selectedId, setSelectedId] = useParam(
    new OptionalStringParam("selected"),
  );
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);

  const currentInvalidSteps = useRef<InvalidFlowStep[]>([]);

  const reactFlowInstance = useReactFlow();

  const centerOnStep = useCallback(
    (stepId: string) => {
      setSelectedId(stepId);
      setShowValidationErrors(true);
      setTimeout(async () => {
        await reactFlowInstance.fitView({
          nodes: [{ id: stepId }],
          duration: 800,
          padding: 0.25,
          maxZoom: 1.2,
        });
      }, 100);
    },
    [setSelectedId, reactFlowInstance],
  );

  useEffect(() => {
    if (!selectedId || !flowInProgress) {
      return undefined;
    }
    const stepExists = flowInProgress.steps.some(
      (step) => step.id === selectedId,
    );
    if (!stepExists) {
      return undefined;
    }
    const timer = setTimeout(() => {
      centerOnStep(selectedId);
    }, 300);

    return () => clearTimeout(timer);
    // Only run on mount if selected param was used
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSelectedIdFromInvalidStep = useCallback(
    (id: string | undefined) => {
      if (id) {
        centerOnStep(id);
      } else {
        setSelectedId(id);
      }
    },
    [centerOnStep, setSelectedId],
  );

  useEffect(() => {
    if (isModifiedFromStart !== undefined) {
      setModified(isModifiedFromStart);
    }
  }, [isModifiedFromStart]);

  const lastExternalFlowChangeSignal = useRef(externalFlowChangeSignal);
  useEffect(() => {
    if (
      externalFlowChangeSignal !== undefined &&
      externalFlowChangeSignal !== lastExternalFlowChangeSignal.current
    ) {
      setModified(true);
    }
    lastExternalFlowChangeSignal.current = externalFlowChangeSignal;
  }, [externalFlowChangeSignal]);

  const hasDefaultContent = !!rightPanel?.defaultContent;

  // Update global notification offset when right panel is opened/closed
  useEffect(() => {
    setNotificationRightOffset(
      selectedId || hasDefaultContent ? 300 : undefined,
    );
  }, [selectedId, hasDefaultContent]);

  useEffect(() => {
    return () => {
      // Reset global offset when component unmounts
      setNotificationRightOffset(undefined);
      for (const step of currentInvalidSteps.current) {
        dismissNotificationByKey(step.id);
      }
    };
  }, []);

  const schema = flowInProgress?.schemaType
    ? schemas[flowInProgress.schemaType]
    : undefined;

  const [saveLoad, doSave] = useTriggerLoad(async (signal: AbortSignal) => {
    if (!flowInProgress || rest.readonly) {
      return null;
    }

    if (rest.onSaveOverride) {
      const result = await rest.onSaveOverride({ flowInProgress, signal });
      if (result && !result.saved) {
        return null;
      }
    } else {
      if (!team || !automationId || !schema) {
        return null;
      }

      await saveAdvancedFlow(
        client,
        flowInProgress.steps as AdvancedFlow["steps"],
        automationId,
        (typeof flowInProgress.name === "string" && flowInProgress.name) ||
          (typeof flowInProgress.name === "object" &&
            flowInProgress.name.value) ||
          getTriggerDisplayName(flowKey) ||
          t("New automation"),
        { signal },
      );
    }
    setModified(false);
    onSaveSuccess?.();
    return null;
  });

  useEffect(() => {
    if (saveLoad.error) {
      enqueueNotification({
        title: t("Failed to save automation"),
        description: t("Your changes could not be saved. Please try again."),
      });
    }
  }, [saveLoad.error, t]);

  const handleSave = () => {
    if (!flowInProgress || rest.readonly || saveDisabled) {
      return;
    }
    doSave();
  };

  const flowKey = flowInProgress?.steps.find(
    (step) => step.type === "trigger",
  )?.key;

  const selectedInt =
    selectedId && flowInProgress
      ? flowInProgress.steps.findIndex((step) => step.id === selectedId)
      : -1;

  const selectedStep =
    selectedInt !== -1 ? (flowInProgress?.steps[selectedInt] ?? null) : null;

  const getStepTitle = useCallback(
    (step: PartialFlowStep): string =>
      stepTypeDisplayNames[step.type] || t("Step"),
    [t],
  );

  const invalidSteps: InvalidFlowStep[] = useMemo(() => {
    if (!flowInProgress) {
      return [];
    }

    const stepsToCheck = partialFlow ?? flowInProgress;
    return stepsToCheck.steps
      .map((step, index) => ({ step, index }))
      .filter(({ step }) => {
        return !step || !stepTypes[step.type].valid(step);
      })
      .map(({ step, index }) => ({
        id: step.id,
        title: getStepTitle(step),
        index,
        type: step.type,
      }));
  }, [flowInProgress, partialFlow, getStepTitle]);

  const disabledStepTypes: AutomationForgeElementTypeWithDisabledTooltip[] =
    useMemo(() => {
      if (!rest.readonly && rest.disabledStepTypes) {
        return rest.disabledStepTypes;
      }

      if (getDisabledStepTypes) {
        return getDisabledStepTypes(flowInProgress, invalidSteps);
      }

      return [];
    }, [invalidSteps, rest, getDisabledStepTypes, flowInProgress]);

  useEffect(() => {
    if (rest.readonly || !manageInvalidStepNotifications) {
      return;
    }

    manageInvalidStepNotifications(
      invalidSteps,
      selectedStep,
      selectedId,
      centerOnStep,
    );
  }, [
    invalidSteps,
    rest.readonly,
    selectedId,
    centerOnStep,
    selectedStep,
    manageInvalidStepNotifications,
  ]);

  useEffect(() => {
    currentInvalidSteps.current = invalidSteps;
  }, [invalidSteps]);

  const setAutomationWithModified: React.Dispatch<
    React.SetStateAction<FlowInProgress | null>
  > = useHandler((action: React.SetStateAction<FlowInProgress | null>) => {
    if (!rest.readonly) {
      rest.setFlowInProgress(action);
    }
    setModified(true);
  });

  const setCurrentStep = useHandler((newStep: PartialFlowStep) => {
    if (selectedInt === undefined || rest.readonly) {
      return;
    }

    rest.setFlowInProgress((prevFlow) => {
      if (!prevFlow) {
        return prevFlow;
      }
      const newSteps = [...prevFlow.steps];
      newSteps[selectedInt] = newStep;
      return { ...prevFlow, steps: newSteps };
    });
    setModified(true);
  });

  const InspectorComponent: React.ReactNode | null = useMemo(() => {
    if (!selectedStep || !flowInProgress) {
      return null;
    }
    const frontendType = stepTypes[selectedStep.type];

    const frontendTypeProps: StepTypeDetailsProps<PartialFlowStep> = {
      category: flowInProgress.category,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      schema: schema!,
      schemaType: flowInProgress.schemaType,
      step: selectedStep,
      steps: flowInProgress.steps,
      useNewDesign: true,
      setStep: setCurrentStep,
      save: rest.readonly ? undefined : handleSave,
      readonly: rest.readonly,
      automationId,
      automation: flowInProgress,
      setAutomation: rest.readonly ? undefined : setAutomationWithModified,
      selectionMode: { mode: selectionMode, setMode: setSelectionMode },
      invalidSteps,
      setSelectedId: setSelectedIdFromInvalidStep,
      showValidationErrors,
      setHoveredNodeId,
      triggerConditions: flowInProgress.triggerConditions,
    };

    if (
      flowInProgress.schemaType === SchemaType.RECOGNIZE &&
      (selectedStep.type === StepType.CONDITION ||
        selectedStep.type === StepType.OMS_CONDITION)
    ) {
      return (
        <RecognizeCondition
          {...frontendTypeProps}
          onClose={() => setSelectedId(undefined)}
          step={selectedStep}
        />
      );
    }

    if (frontendType.type === "custom") {
      // Pass onClose and setAutomation to custom inspectors via props
      return (
        <frontendType.customInspector
          {...frontendTypeProps}
          key={selectedStep.id}
          onClose={() => setSelectedId(undefined)}
          setAutomation={rest.readonly ? undefined : setAutomationWithModified}
        />
      );
    } else if (frontendType.type === "standard") {
      const config = frontendType.inspectorFactory(frontendTypeProps);

      const configWithClose = {
        ...config,
        onClose: () => setSelectedId(undefined),
      };

      return (
        <Inspector
          config={configWithClose}
          key={selectedStep.id}
          object={selectedStep}
          setObject={(state) => setCurrentStep(state as PartialFlowStep)}
        />
      );
    } else {
      assertNever(frontendType);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedStep,
    flowInProgress,
    schema,
    selectionMode,
    setSelectionMode,
    rest.readonly,
    saveDisabled,
    modified,
    showValidationErrors,
    setHoveredNodeId,
  ]);

  // Delay the graph update to avoid an intermittent React Flow render bug.
  const [realFlowInProgress, setRealFlowInProgress] =
    useState<FlowInProgress | null>(null);
  useLayoutEffect(() => {
    setRealFlowInProgress(flowInProgress);
  }, [flowInProgress]);

  const onTitleChange = rest.readonly
    ? undefined
    : (title: string) => {
        rest.setFlowInProgress((prevFlow) => {
          if (!prevFlow) {
            return prevFlow;
          }
          return { ...prevFlow, name: title };
        });
        setModified(true);
      };

  const onClose = useHandler(() => {
    // Preserve deep-link return paths when provided.
    const returnTo = new URLSearchParams(location.search).get("returnTo");
    if (returnTo && returnTo.startsWith("/")) {
      navigate(returnTo);
      return;
    }
    navigate("..");
  });

  const headerButtons: FormBuilderHeaderButton[] = (() => {
    const onSaveDisabled =
      !!header?.disableButtons ||
      !realFlowInProgress ||
      !!invalidSteps.length ||
      !!saveDisabled ||
      !hasUnsavedChanges ||
      saveLoad.pending;

    // Generate tooltip message explaining why save is disabled
    const saveDisabledTooltip = onSaveDisabled
      ? (() => {
          if (!realFlowInProgress) {
            return t("Flow not loaded");
          }
          if (saveDisabled) {
            return t("Fix the JSON validation errors before saving");
          }
          if (saveLoad.pending) {
            return t("Saving...");
          }
          if (!hasUnsavedChanges) {
            return t("No changes to save");
          }
          if (invalidSteps.length > 0) {
            const stepNames = invalidSteps
              .slice(0, 3)
              .map((s) => s.title)
              .join(", ");
            const remaining = invalidSteps.length - 3;
            return remaining > 0
              ? t(
                  "Fix validation errors in: {{stepNames}} and {{remaining}} more",
                  { remaining, stepNames },
                )
              : t("Fix validation errors in: {{stepNames}}", { stepNames });
          }
          return t("Cannot save");
        })()
      : undefined;

    const saveButton: FormBuilderHeaderButton | undefined = rest.readonly
      ? undefined
      : {
          text: saveButtonText ?? t("Save changes"),
          onClick: handleSave,
          hierarchy: "primary",
          disabled: onSaveDisabled,
          pending: saveLoad.pending,
          size: "sm",
          tooltip: saveDisabledTooltip,
          dataTrackId: saveButtonDataTrackId,
        };

    const defaultButtons = filterTruthy([
      {
        text: rest.closeButtonText ?? t("Close"),
        onClick: onCloseOverride ?? onClose,
        disabled: header?.disableButtons,
        hierarchy: "secondary" as const,
        size: "sm" as const,
      },
      saveButton,
    ]);

    const buttons: FormBuilderHeaderButton[] = [...defaultButtons];
    const additionalButtons: FormBuilderHeaderButton[] = (() => {
      if (!additionalHeaderButtons) {
        return [];
      } else if (typeof additionalHeaderButtons.buttons === "function") {
        return additionalHeaderButtons.buttons({ onSaveDisabled });
      } else if (typeof additionalHeaderButtons.buttons === "object") {
        return additionalHeaderButtons.buttons;
      } else {
        return [];
      }
    })();
    if (additionalButtons.length > 0) {
      buttons.splice(
        additionalHeaderButtons?.position ?? 0,
        0,
        ...additionalButtons,
      );
    }

    return buttons;
  })();

  // Create a handler for node clicks based on current selection mode
  const handleNodeClick = useCallback(
    (nodeId: string | undefined) => {
      switch (selectionMode.type) {
        case "normal":
          setSelectedId(nodeId);
          if (nodeId !== selectedId) {
            const isInvalidNode = invalidSteps.some(
              (step) => step.id === nodeId,
            );
            setShowValidationErrors(isInvalidNode || false);
          }
          break;
        case "node-copy":
          if (nodeId) {
            selectionMode.onNodeSelect(nodeId);
          } else {
            // Clicking outside in node-copy mode cancels the operation
            setSelectionMode({ type: "normal" });
          }
          break;
        case "node-copy-subtree":
          if (nodeId) {
            selectionMode.onNodeSelect(nodeId);
          } else {
            // Clicking outside in node-copy-subtree mode cancels the operation
            setSelectionMode({ type: "normal" });
          }
          break;
        default:
          assertNever(selectionMode);
      }
    },
    [selectionMode, setSelectionMode, setSelectedId, invalidSteps, selectedId],
  );

  const flowChartProps = useMemo(() => {
    if (!realFlowInProgress) {
      return null;
    }

    const commonProps = {
      automation: realFlowInProgress,
      selected: selectedId,
      onNodeClick: handleNodeClick,
      partialFlow,
      elementDrop,
      deleteElementOverride,
      showMiniMap,
      hoveredNodeId,
      collapsibleNodes,
      confirmCascadingDelete,
    };

    if (rest.readonly) {
      return {
        ...commonProps,
        readonly: true as const,
        setAutomation: undefined,
        setModified: undefined,
      };
    } else {
      return {
        ...commonProps,
        readonly: false as const,
        setAutomation: setAutomationWithModified,
        setModified,
      };
    }
  }, [
    handleNodeClick,
    realFlowInProgress,
    selectedId,
    partialFlow,
    elementDrop,
    deleteElementOverride,
    rest,
    setAutomationWithModified,
    setModified,
    showMiniMap,
    hoveredNodeId,
    collapsibleNodes,
    confirmCascadingDelete,
  ]);

  let selectedInspector = selectedId ? InspectorComponent : null;

  if (rest.readonly && flowInProgress?.schemaType === SchemaType.RECOGNIZE) {
    selectedInspector = (
      <div {...{ inert: "" }} className={flowEditorStyles.readonlyInspector}>
        {selectedInspector}
      </div>
    );
  }

  if (!realFlowInProgress || !flowChartProps) {
    return (
      <Flex align="center" justify="center" w="full">
        <LoadingRedoAnimation />
      </Flex>
    );
  }

  return (
    <FormBuilderPageWrapper>
      {!header?.hide && (
        <FormBuilderHeader
          buttons={headerButtons}
          compact={header?.compact}
          onTitleChange={
            typeof realFlowInProgress.name === "object" &&
            realFlowInProgress.name.readonly
              ? undefined
              : realFlowInProgress.category !== "Upsell"
                ? onTitleChange
                : undefined
          }
          title={
            (typeof realFlowInProgress.name === "string" &&
              realFlowInProgress.name) ||
            (typeof realFlowInProgress.name === "object" &&
              realFlowInProgress.name.value) ||
            getTriggerDisplayName(flowKey) ||
            t("Loading...")
          }
        />
      )}
      <Flex
        dir="row"
        flex={1}
        gap="none"
        justify="space-between"
        overflow="hidden"
        position="relative"
        w="full"
      >
        {!leftPanel?.hide && !rest.readonly && (
          <FlowEditorLeftPanel
            bgColor={leftPanel?.bgColor}
            description={leftPanel?.description}
            disabledStepTypes={disabledStepTypes}
            invalidSteps={invalidSteps}
            onGoToStep={centerOnStep}
            renderInvalidStepAlert={renderInvalidStepAlert}
            stepOptions={rest.stepOptions}
            title={leftPanel?.title}
          />
        )}
        {schema && <Flowchart schema={schema} {...flowChartProps} />}
        <RightPanelWithAnimation
          bgColor={rightPanel?.bgColor}
          defaultContent={
            typeof rightPanel?.defaultContent === "function"
              ? rightPanel.defaultContent(setAutomationWithModified)
              : rightPanel?.defaultContent
          }
          InspectorComponent={selectedInspector}
          show={!!selectedId || !!rightPanel?.defaultContent}
        />
      </Flex>
      {confirmOpen && (
        <UnsavedChangesModal
          onDiscard={discardChanges}
          onKeepEditing={keepEditing}
        />
      )}
    </FormBuilderPageWrapper>
  );
});

export const FlowEditor = memo(function FlowEditor(props: FlowEditorProps) {
  return (
    <ReactFlowProvider>
      <ConditionValueLabelsProvider>
        <FlowEditorContent {...props} />
      </ConditionValueLabelsProvider>
    </ReactFlowProvider>
  );
});
